import React, { useState, useMemo } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Box,
  useTheme,
  useMediaQuery,
  TextField,
  InputAdornment,
  IconButton,
  Menu,
  MenuItem,
  Checkbox,
  ListItemText,
  Button,
  Chip,
} from "@mui/material";
import FilterListIcon from "@mui/icons-material/FilterList";
import ClearIcon from "@mui/icons-material/Clear";
import SearchIcon from "@mui/icons-material/Search";
import { reportColumnsConfig, getNestedValue, formatValue } from "./reportColumnsConfig";

const ReportDataTable = ({ reportKey, data }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  
  // حالات الفلاتر
  const [columnFilters, setColumnFilters] = useState({});
  const [searchTerm, setSearchTerm] = useState("");
  const [anchorEl, setAnchorEl] = useState(null);
  const [activeFilterColumn, setActiveFilterColumn] = useState(null);

  const config = reportColumnsConfig[reportKey];

  // الحصول على القيم الفريدة لكل عمود
  const getUniqueValues = (columnKey, isNested) => {
    if (!data || !Array.isArray(data)) return [];
    const values = data.map(row => {
      const value = isNested ? getNestedValue(row, columnKey) : row[columnKey];
      return formatValue(value);
    });
    return [...new Set(values)].filter(v => v !== "-").sort();
  };

  // تطبيق الفلاتر على البيانات
  const filteredData = useMemo(() => {
    if (!data || !Array.isArray(data) || !config) return [];
    
    let filtered = [...data];

    // تطبيق فلاتر الأعمدة
    Object.entries(columnFilters).forEach(([columnKey, selectedValues]) => {
      if (selectedValues.length > 0) {
        filtered = filtered.filter(row => {
          const col = config.columns.find(c => c.key === columnKey);
          const value = col.nested ? getNestedValue(row, columnKey) : row[columnKey];
          const formattedValue = formatValue(value, col.format);
          return selectedValues.includes(formattedValue);
        });
      }
    });

    // تطبيق البحث الشامل
    if (searchTerm.trim()) {
      filtered = filtered.filter(row => {
        return config.columns.some(col => {
          const value = col.nested ? getNestedValue(row, col.key) : row[col.key];
          const formattedValue = formatValue(value, col.format);
          return formattedValue.toLowerCase().includes(searchTerm.toLowerCase());
        });
      });
    }

    return filtered;
  }, [data, columnFilters, searchTerm, config]);

  const displayData = filteredData.slice(0, 100);

  // عدد الفلاتر النشطة
  const activeFiltersCount = Object.keys(columnFilters).length + (searchTerm ? 1 : 0);

  // فتح قائمة الفلتر
  const handleFilterClick = (event, columnKey) => {
    setAnchorEl(event.currentTarget);
    setActiveFilterColumn(columnKey);
  };

  // إغلاق قائمة الفلتر
  const handleFilterClose = () => {
    setAnchorEl(null);
    setActiveFilterColumn(null);
  };

  // تبديل قيمة الفلتر
  const handleFilterToggle = (columnKey, value) => {
    setColumnFilters(prev => {
      const currentFilters = prev[columnKey] || [];
      const newFilters = currentFilters.includes(value)
        ? currentFilters.filter(v => v !== value)
        : [...currentFilters, value];
      
      if (newFilters.length === 0) {
        const { [columnKey]: removed, ...rest } = prev;
        return rest;
      }
      
      return { ...prev, [columnKey]: newFilters };
    });
  };

  // مسح فلتر عمود معين
  const clearColumnFilter = (columnKey) => {
    setColumnFilters(prev => {
      const { [columnKey]: removed, ...rest } = prev;
      return rest;
    });
  };

  // مسح جميع الفلاتر
  const clearAllFilters = () => {
    setColumnFilters({});
    setSearchTerm("");
  };

  // التحقق من البيانات بعد جميع الـ Hooks
  if (!data || !Array.isArray(data) || data.length === 0) {
    return (
      <Typography 
        variant="body2" 
        color="text.secondary" 
        sx={{ 
          p: { xs: 2, md: 3 },
          fontSize: { xs: '0.9rem', md: '1rem' },
          textAlign: 'center'
        }}
      >
        لا توجد بيانات
      </Typography>
    );
  }

  if (!config) {
    return (
      <Typography 
        variant="body2" 
        color="error" 
        sx={{ 
          p: { xs: 2, md: 3 },
          fontSize: { xs: '0.9rem', md: '1rem' }
        }}
      >
        تكوين غير موجود لهذا التقرير
      </Typography>
    );
  }

  // عرض مبسط للموبايل (نمط البطاقات)
  if (isMobile) {
    return (
      <Box>
        {/* شريط البحث والفلاتر */}
        <Box sx={{ mb: 2, px: 1 }}>
          <TextField
            fullWidth
            size="small"
            placeholder="البحث في جميع الأعمدة..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
              endAdornment: searchTerm && (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setSearchTerm("")}>
                    <ClearIcon />
                  </IconButton>
                </InputAdornment>
              )
            }}
            sx={{ fontSize: '0.85rem' }}
          />
          {activeFiltersCount > 0 && (
            <Box sx={{ mt: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Chip 
                label={`${activeFiltersCount} فلتر نشط`} 
                size="small" 
                color="primary" 
              />
              <Button size="small" onClick={clearAllFilters}>
                مسح الكل
              </Button>
            </Box>
          )}
        </Box>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {displayData.map((row, idx) => (
            <Paper 
              key={idx} 
              elevation={2} 
              sx={{ 
                p: 2,
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'divider'
              }}
            >
              {config.columns.map((col) => {
                const value = col.nested
                  ? getNestedValue(row, col.key)
                  : row[col.key];
                const formattedValue = formatValue(value, col.format);
                
                return (
                  <Box 
                    key={col.key}
                    sx={{ 
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'flex-start',
                      py: 0.75,
                      borderBottom: '1px solid',
                      borderColor: 'divider',
                      '&:last-child': {
                        borderBottom: 'none'
                      }
                    }}
                  >
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        fontWeight: 600,
                        fontSize: '0.85rem',
                        color: 'text.secondary',
                        flex: '0 0 45%'
                      }}
                    >
                      {col.label}:
                    </Typography>
                    <Typography 
                      variant="body2"
                      sx={{ 
                        fontSize: '0.875rem',
                        flex: '0 0 55%',
                        textAlign: 'right',
                        wordBreak: 'break-word'
                      }}
                    >
                      {formattedValue}
                    </Typography>
                  </Box>
                );
              })}
            </Paper>
          ))}
        </Box>
        {filteredData.length > 100 && (
          <Typography 
            variant="body2" 
            color="text.secondary" 
            sx={{ 
              mt: 2, 
              textAlign: "center",
              fontSize: '0.85rem'
            }}
          >
            عرض 100 من {filteredData.length} سجل
          </Typography>
        )}
        {filteredData.length === 0 && (
          <Typography 
            variant="body2" 
            color="text.secondary" 
            sx={{ 
              mt: 2, 
              textAlign: "center",
              fontSize: '0.85rem'
            }}
          >
            لا توجد نتائج مطابقة للفلاتر
          </Typography>
        )}
      </Box>
    );
  }

  // عرض الجدول للتابلت والديسكتوب
  return (
    <Box>
      {/* شريط البحث والفلاتر */}
      {/* <Box sx={{ mb: 2, display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
        <TextField
          size="small"
          placeholder="البحث في جميع الأعمدة..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
            endAdornment: searchTerm && (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => setSearchTerm("")}>
                  <ClearIcon />
                </IconButton>
              </InputAdornment>
            )
          }}
          sx={{ minWidth: 250, fontSize: { sm: '0.85rem', md: '0.9rem' } }}
        />
        {activeFiltersCount > 0 && (
          <>
            <Chip 
              label={`${activeFiltersCount} فلتر نشط`} 
              size="small" 
              color="primary" 
            />
            <Button 
              size="small" 
              startIcon={<ClearIcon />} 
              onClick={clearAllFilters}
              sx={{ fontSize: { sm: '0.8rem', md: '0.85rem' } }}
            >
              مسح جميع الفلاتر
            </Button>
          </>
        )}
        <Typography 
          variant="body2" 
          color="text.secondary"
          sx={{ ml: 'auto', fontSize: { sm: '0.85rem', md: '0.9rem' } }}
        >
          عرض {displayData.length} من {filteredData.length} سجل
        </Typography>
      </Box> */}

      <TableContainer
        component={Paper}
        dir="rtl"
        sx={{
          maxHeight: { sm: 500, md: 600 },
          overflowX: 'auto',
          boxShadow: 1,
          direction: 'rtl' // يجعل المحتوى RTL
        }}
      >
        <Table
          stickyHeader
          size={isTablet ? "small" : "medium"}
          sx={{
            minWidth: { sm: 650, md: 800 },
            // خطوط رأسية بين الأعمدة، مع إزالة الخط في آخر عمود
            '& th, & td': {
              borderRight: '1px solid',
              borderColor: 'divider'
            },
            '& th:last-of-type, & td:last-of-type': {
              borderRight: 'none'
            },
            // اجعل النص في الخلايا إلى اليمين ليتناسب مع RTL
            '& td, & th': {
              textAlign: 'right'
            }
          }}
        >
          <TableHead>
            <TableRow>
              {config.columns.map((col) => {
                const hasFilter = columnFilters[col.key]?.length > 0;
                return (
                  <TableCell
                    key={col.key}
                    sx={{
                      fontWeight: "bold",
                      backgroundColor: hasFilter ? "primary.dark" : "primary.main",
                      color: "white",
                      minWidth: isTablet ? col.width * 0.8 : col.width,
                      fontSize: { sm: '0.85rem', md: '0.95rem' },
                      py: { sm: 1.5, md: 2 },
                      whiteSpace: 'nowrap',
                      position: 'relative'
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <span>{col.label}</span>
                      <IconButton
                        size="small"
                        onClick={(e) => handleFilterClick(e, col.key)}
                        sx={{ 
                          color: 'white',
                          '&:hover': { backgroundColor: 'rgba(255,255,255,0.1)' }
                        }}
                      >
                        <FilterListIcon fontSize="small" />
                        {hasFilter && (
                          <Box
                            sx={{
                              position: 'absolute',
                              top: 2,
                              right: 2,
                              width: 8,
                              height: 8,
                              borderRadius: '50%',
                              backgroundColor: 'error.main'
                            }}
                          />
                        )}
                      </IconButton>
                    </Box>
                  </TableCell>
                );
              })}
            </TableRow>
          </TableHead>
          <TableBody>
            {displayData.map((row, idx) => (
              <TableRow 
                key={idx} 
                hover
                sx={{
                  '&:nth-of-type(odd)': {
                    backgroundColor: 'action.hover',
                  },
                  '&:hover': {
                    backgroundColor: 'action.selected',
                  }
                }}
              >
                {config.columns.map((col) => {
                  const value = col.nested
                    ? getNestedValue(row, col.key)
                    : row[col.key];
                  const formattedValue = formatValue(value, col.format);
                  
                  return (
                    <TableCell 
                      key={col.key}
                      sx={{
                        fontSize: { sm: '0.8rem', md: '0.9rem' },
                        py: { sm: 1, md: 1.5 },
                        maxWidth: { sm: 150, md: 250 },
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: { sm: 'nowrap', md: 'normal' },
                        // في حالة الحاجة لفصل النص عن الحد
                        pr: 1,
                        pl: 1
                      }}
                    >
                      {formattedValue}
                    </TableCell>
                  );
                })}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {filteredData.length === 0 && (
        <Typography 
          variant="body2" 
          color="text.secondary" 
          sx={{ 
            mt: 2, 
            textAlign: "center",
            fontSize: { sm: '0.85rem', md: '0.9rem' }
          }}
        >
          لا توجد نتائج مطابقة للفلاتر
        </Typography>
      )}

      {filteredData.length > 100 && (
        <Typography 
          variant="body2" 
          color="text.secondary" 
          sx={{ 
            mt: { sm: 1.5, md: 2 },
            textAlign: "center",
            fontSize: { sm: '0.85rem', md: '0.9rem' }
          }}
        >
          عرض 100 من {filteredData.length} سجل
        </Typography>
      )}

      {/* قائمة الفلتر */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleFilterClose}
        PaperProps={{
          sx: {
            maxHeight: 400,
            width: 300,
          }
        }}
      >
        {activeFilterColumn && (
          <>
            <Box sx={{ px: 2, py: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
              <Typography variant="subtitle2" fontWeight="bold">
                فلترة: {config.columns.find(c => c.key === activeFilterColumn)?.label}
              </Typography>
              {columnFilters[activeFilterColumn]?.length > 0 && (
                <Button 
                  size="small" 
                  onClick={() => clearColumnFilter(activeFilterColumn)}
                  sx={{ mt: 0.5, fontSize: '0.75rem' }}
                >
                  مسح الفلتر
                </Button>
              )}
            </Box>
            {getUniqueValues(
              activeFilterColumn, 
              config.columns.find(c => c.key === activeFilterColumn)?.nested
            ).map((value) => (
              <MenuItem 
                key={value}
                onClick={() => handleFilterToggle(activeFilterColumn, value)}
                sx={{ py: 0.5 }}
              >
                <Checkbox 
                  checked={columnFilters[activeFilterColumn]?.includes(value) || false}
                  size="small"
                />
                <ListItemText 
                  primary={value} 
                  primaryTypographyProps={{ fontSize: '0.85rem' }}
                />
              </MenuItem>
            ))}
          </>
        )}
      </Menu>
    </Box>
  );
};

export default ReportDataTable;
